/**
 * Form primitives (seam 2 of the UI redesign): a labelled field, the text
 * input, the textarea, the select, the switch and the segmented control —
 * the same 13px type, the same hairlines, the same 4px radius as the rest of
 * the frame. Every control is controlled by its caller: value in, `onChange`
 * with the next value out.
 *
 * No `useState`, no `useEffect`.
 *
 * @since 1.0.0
 */
import type { ReactNode } from "react";

/** @since 1.0.0 */
export interface FieldProps {
  readonly children: ReactNode;
  /** The error under the control; also turns the label red. */
  readonly error?: ReactNode;
  /** The muted line under the label. */
  readonly hint?: ReactNode;
  readonly label: ReactNode;
  readonly testId?: string;
}

/** A labelled control: label, hint, the control, then its error. @since 1.0.0 */
export const Field = ({ children, error, hint, label, testId }: FieldProps): ReactNode => (
  <label className={error === undefined ? "ol-field" : "ol-field ol-bad"} data-testid={testId}>
    <span className="ol-fl">{label}</span>
    {hint === undefined ? null : <span className="ol-fh">{hint}</span>}
    {children}
    {error === undefined ? null : <span className="ol-fe" role="alert">{error}</span>}
  </label>
);

/** @since 1.0.0 */
export interface InputProps {
  readonly autoComplete?: string;
  readonly autoFocus?: boolean;
  readonly disabled?: boolean;
  readonly inputMode?: "text" | "numeric" | "email" | "url";
  /** Draws the red ring without a {@link Field} error. */
  readonly invalid?: boolean;
  readonly name?: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly testId?: string;
  readonly type?: "text" | "email" | "password" | "url" | "number";
  readonly value: string;
}

/** The text input. @since 1.0.0 */
export const Input = (
  {
    autoComplete,
    autoFocus = false,
    disabled = false,
    inputMode,
    invalid = false,
    name,
    onChange,
    placeholder,
    testId,
    type = "text",
    value,
  }: InputProps,
): ReactNode => (
  <input
    aria-invalid={invalid ? "true" : undefined}
    autoComplete={autoComplete}
    autoFocus={autoFocus}
    className={invalid ? "ol-input ol-bad" : "ol-input"}
    data-testid={testId}
    disabled={disabled}
    inputMode={inputMode}
    name={name}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    type={type}
    value={value}
  />
);

/** @since 1.0.0 */
export interface TextareaProps {
  readonly autoFocus?: boolean;
  readonly disabled?: boolean;
  readonly invalid?: boolean;
  readonly name?: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  /** Rows the box opens at; it resizes vertically from there. */
  readonly rows?: number;
  readonly testId?: string;
  readonly value: string;
}

/** The textarea. @since 1.0.0 */
export const Textarea = (
  { autoFocus = false, disabled = false, invalid = false, name, onChange, placeholder, rows = 3, testId, value }:
    TextareaProps,
): ReactNode => (
  <textarea
    aria-invalid={invalid ? "true" : undefined}
    autoFocus={autoFocus}
    className={invalid ? "ol-textarea ol-bad" : "ol-textarea"}
    data-testid={testId}
    disabled={disabled}
    name={name}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    rows={rows}
    value={value}
  />
);

/** @since 1.0.0 */
export interface SelectOption {
  readonly label: string;
  readonly value: string;
}

/** @since 1.0.0 */
export interface SelectProps {
  readonly disabled?: boolean;
  readonly name?: string;
  readonly onChange: (value: string) => void;
  readonly options: ReadonlyArray<SelectOption>;
  readonly testId?: string;
  readonly value: string;
}

/** The native select in the frame's clothes. @since 1.0.0 */
export const Select = ({ disabled = false, name, onChange, options, testId, value }: SelectProps): ReactNode => (
  <select
    className="ol-select"
    data-testid={testId}
    disabled={disabled}
    name={name}
    onChange={(event) => onChange(event.target.value)}
    value={value}
  >
    {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
  </select>
);

/** @since 1.0.0 */
export interface ToggleProps {
  readonly disabled?: boolean;
  readonly label: ReactNode;
  readonly on: boolean;
  readonly onChange: (on: boolean) => void;
  readonly testId?: string;
}

/** A switch with its label on the right. @since 1.0.0 */
export const Toggle = ({ disabled = false, label, on, onChange, testId }: ToggleProps): ReactNode => (
  <button
    aria-checked={on}
    className={on ? "ol-toggle ol-on" : "ol-toggle"}
    data-testid={testId}
    disabled={disabled}
    onClick={() => onChange(!on)}
    role="switch"
    type="button"
  >
    <span aria-hidden="true" className="ol-tk" />
    <span className="ol-tl">{label}</span>
  </button>
);

/** @since 1.0.0 */
export interface SegmentedOption {
  /** The key printed beside the label. */
  readonly kbd?: string;
  readonly label: string;
  readonly value: string;
}

/** @since 1.0.0 */
export interface SegmentedProps {
  readonly onChange: (value: string) => void;
  readonly options: ReadonlyArray<SegmentedOption>;
  readonly testId?: string;
  readonly value: string;
}

/** A segmented control: one pressed segment at a time. @since 1.0.0 */
export const Segmented = ({ onChange, options, testId, value }: SegmentedProps): ReactNode => (
  <div className="ol-seg" data-testid={testId} role="group">
    {options.map((option) => (
      <button
        aria-pressed={option.value === value}
        className={option.value === value ? "ol-on" : undefined}
        data-testid={testId === undefined ? undefined : `${testId}-${option.value}`}
        key={option.value}
        onClick={() => onChange(option.value)}
        type="button"
      >
        {option.label}
        {option.kbd === undefined ? null : <kbd className="ol-k">{option.kbd}</kbd>}
      </button>
    ))}
  </div>
);
